import { type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import type { Row } from '@tanstack/react-table';
import type { ServiceCallError } from 'api/ServiceCallError';
import clsx from 'clsx';
import { type JSX, useCallback, useMemo, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { PerspectiveLoadingIndicator } from 'ts/base/components/PerspectiveLoadingIndicator';
import { useCommit } from 'ts/base/hooks/UseCommit';
import { useNavigationHash } from 'ts/base/hooks/UseNavigationHash';
import { RemoteSortableTable } from 'ts/base/table/RemoteSortableTable';
import type { SortOptions } from 'ts/base/table/SortableTable';
import { EnumUtils } from 'ts/commons/EnumUtils';
import type { ProjectAndUniformPath } from 'ts/commons/ProjectAndUniformPath';
import { IssueQueryError } from 'ts/commons/query/IssueLikeQueryResultsTable';
import { useDeriveVisibleColumns } from 'ts/commons/TableUtils';
import { Message } from 'ts/components/Message';
import type { TableRowProps } from 'ts/components/Table';
import { useScrollPositionRestoration } from 'ts/perspectives/activity/commits/UseScrollPositionRestoration';
import { useFindingsContextMenu } from 'ts/perspectives/findings/list/FindingContextMenu';
import { useFindingsTableColumnDefinition } from 'ts/perspectives/findings/list/FindingsTable.columns';
import { ESortOrder } from 'typedefs/ESortOrder';
import { ETrafficLightColor } from 'typedefs/ETrafficLightColor';
import type { ExtendedTrackedFinding } from 'typedefs/ExtendedTrackedFinding';
import type { PaginatedFindingsWithCount } from 'typedefs/PaginatedFindingsWithCount';
import type { SpecItem } from 'typedefs/SpecItem';
import styles from './FindingsTable.module.less';

/** Table with column IDs and respective labels. For columns that are not included the IDs and labels are the same. */
export const COLUMN_LABEL_BY_ID = new Map<string, string>([
	['message', 'Message'],
	['uniformPath', 'Location'],
	['group', 'Category'],
	['birth', 'Introduction Date'],
	['tasks', 'Tasks'],
	['architecture', 'Architecture']
]);

/** Props for FindingsTable. */
type FindingsTableProps = {
	projectAndPath: ProjectAndUniformPath;
	searchParamsId: string;
	hiddenColumns: string[];
	sortOptions: SortOptions;
	onSortOptionsChanged: Callback<SortOptions>;
	findingsQueryResult: UseInfiniteQueryResult<InfiniteData<PaginatedFindingsWithCount>, ServiceCallError>;
};

/**
 * Renders the findings table for the findings list view with endless scroll support, remote sorting, row selection and
 * a context menu.
 */
export function FindingsTable({
	searchParamsId,
	projectAndPath,
	hiddenColumns,
	sortOptions,
	onSortOptionsChanged,
	findingsQueryResult
}: FindingsTableProps): JSX.Element | null {
	const commit = useCommit();
	const { data, isPending, error, fetchNextPage, hasNextPage } = findingsQueryResult;
	const isSolvedIconShown = !commit.isLatestRevision();
	const { contextMenu, modal, onContextMenu } = useFindingsContextMenu(commit);
	const commonProperties = data?.pages[0]?.commonProperties;

	const findingsAndIssues = useMemo<{
		findings: ExtendedTrackedFinding[];
		referencedSpecItems: Record<string, SpecItem>;
	}>(() => {
		const referencedSpecItems: Record<string, SpecItem> = {};
		if (isPending || error) {
			return {
				findings: [],
				referencedSpecItems
			};
		}
		const findings = data.pages.flatMap(page => page.findings);
		data.pages.forEach(page => Object.assign(referencedSpecItems, page.referencedSpecItems));
		return {
			findings,
			referencedSpecItems
		};
	}, [data, isPending, error]);

	const findingColumnDefinitions = useFindingsTableColumnDefinition(
		commit,
		isSolvedIconShown,
		projectAndPath.getProject(),
		projectAndPath.getPath(),
		searchParamsId,
		commonProperties,
		onContextMenu,
		findingsAndIssues.referencedSpecItems
	);
	const restoreScroll = useScrollPositionRestoration();
	const columnVisibility = useDeriveVisibleColumns(findingColumnDefinitions, hiddenColumns);
	// eslint-disable-next-line react-hooks/immutability
	columnVisibility.tasks = Boolean(data?.pages[0]?.hasFindingsWithTasks) && !hiddenColumns.includes('tasks');
	const flatData = useMemo(() => {
		if (isPending || error) {
			return [];
		}
		return data.pages.flatMap(page => page.findings);
	}, [data, isPending, error]);

	if (isPending) {
		return <PerspectiveLoadingIndicator />;
	}
	if (error) {
		return <IssueQueryError error={error as Error} />;
	}
	if (flatData.length === 0) {
		return <Message>No findings to display.</Message>;
	}
	return (
		<>
			{contextMenu}
			{modal}
			<RemoteSortableTable
				ref={restoreScroll}
				compact
				selectable
				striped
				unstackable
				borderless
				framed
				onFetchNext={fetchNextPage}
				hasNextPage={hasNextPage}
				sorting={sortOptions}
				data={flatData}
				columnDefinitions={findingColumnDefinitions}
				onSortOptionsChanged={onSortOptionsChanged}
				data-testid="findings-table"
				className={styles.findingsTable}
				getRowProps={getFindingTableRowProps}
				resizeableContainerProps={{ className: styles.findingsTableContainer }}
				tableOptions={{
					enableRowSelection: true,
					enableHiding: true,
					enableColumnResizing: true,
					state: { columnVisibility },
					getRowId: row => row.id
				}}
			/>
		</>
	);
}

/**
 * Custom React hook for managing sort options of a table.
 *
 * @param initialSorting The initial sorting of the table.
 * @param updateSearchParams This function is called, when a column is sorted.
 */
export function useHashSortOptionsWithFallback(
	initialSorting: NonNullable<SortOptions>
): [NonNullable<SortOptions>, (sortOptions: SortOptions) => void] {
	const navigationHash = useNavigationHash();
	// State to track if the first sort on the "Value" column has been done after applying a filter.
	const [isValueColFirstSort, setIsValueColFirstSort] = useState(false);
	const sortOptions = useMemo(
		() => ({
			sortByField: navigationHash.getString('sort-by') ?? initialSorting.sortByField,
			sortOrder: EnumUtils.fromValue(navigationHash.getString('sort-order'), ESortOrder, initialSorting.sortOrder)
		}),
		[navigationHash, initialSorting]
	);
	const setSortOptionsWithHash = useCallback(
		(options: SortOptions) => {
			let effectiveSortOrder = options.sortOrder;

			// If the selected column to sort by is not 'Value', reset the interaction flag.
			if (options.sortByField !== 'Value') {
				setIsValueColFirstSort(false);
			} else if (!isValueColFirstSort) {
				// If it is the first sort on 'Value', default to descending.
				effectiveSortOrder = ESortOrder.DESCENDING;
				setIsValueColFirstSort(true);
			} else {
				// Toggle sort order on subsequent interactions after the first sort.
				effectiveSortOrder =
					sortOptions.sortOrder === ESortOrder.ASCENDING ? ESortOrder.DESCENDING : ESortOrder.ASCENDING;
			}
			// Only update the search params if there is an actual change to sort settings.
			if (options.sortByField !== sortOptions.sortByField || effectiveSortOrder !== sortOptions.sortOrder) {
				navigationHash.set('sort-by', options.sortByField);
				navigationHash.set('sort-order', effectiveSortOrder.name);
				navigationHash.applyUrlWithoutReload();
			}
		},
		[navigationHash, sortOptions, isValueColFirstSort]
	);
	return [sortOptions, setSortOptionsWithHash];
}

/** Provides the props that should be set on all findings table rows. */
export function getFindingTableRowProps<D extends ExtendedTrackedFinding>(row: Row<D>): TableRowProps {
	return {
		className: clsx({
			negative: row.original.assessment === ETrafficLightColor.RED.name,
			[styles.selectedResizableTableRow!]: row.getIsSelected(),
			[styles.strikeTrough!]: Boolean(row.original.death)
		}),
		'data-finding-id': row.original.id
	};
}
